import { useEffect, useMemo, useState } from 'react';
import { Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { BacktestComparisonResult, ComparisonEvent } from '@/api/mediumTermBacktest';
import PortfolioSelector from './PortfolioSelector';
import {
  ACTION_LABELS,
  CONSTRAINT_LABELS,
  INTENT_LABELS,
  constraintText,
  eventReason,
  formatNumber,
  formatQuantity,
  formatSigned,
  portfolioOptions,
  shortDate,
} from './viewModel';
import type { PortfolioKey } from './viewModel';

type ExecutionFilter = 'FILLED' | 'UNEXECUTED' | 'ALL';

interface PortfolioLedgerProps {
  result: BacktestComparisonResult;
  portfolio: PortfolioKey;
  onPortfolioChange: (value: PortfolioKey) => void;
}

function eventMatchesExecution(event: ComparisonEvent, filter: ExecutionFilter): boolean {
  if (filter === 'FILLED') return event.filled_quantity_mwh > 0;
  if (filter === 'UNEXECUTED') {
    return event.filled_quantity_mwh <= 0 || event.unfilled_quantity_mwh > 0;
  }
  return true;
}

export default function PortfolioLedger({
  result,
  portfolio,
  onPortfolioChange,
}: PortfolioLedgerProps) {
  const options = useMemo(() => portfolioOptions(result), [result]);
  const selected = options.find((option) => option.key === portfolio) ?? options[0];
  const [executionFilter, setExecutionFilter] = useState<ExecutionFilter>('FILLED');
  const [targetDate, setTargetDate] = useState('ALL');
  const [windowIndex, setWindowIndex] = useState('ALL');

  useEffect(() => {
    setTargetDate('ALL');
    setWindowIndex('ALL');
  }, [portfolio]);

  const targetOptions = Array.from(new Set(selected.path.events.map((event) => event.target_date)))
    .sort()
    .map((value) => ({ value, label: value }));
  const windowOptions = Array.from(new Set(selected.path.events.map((event) => event.window_index)))
    .sort((a, b) => a - b)
    .map((value) => ({ value: String(value), label: value ? `W${value}` : '基线' }));

  const events = selected.path.events.filter((event) => (
    eventMatchesExecution(event, executionFilter)
    && (targetDate === 'ALL' || event.target_date === targetDate)
    && (windowIndex === 'ALL' || String(event.window_index) === windowIndex)
  ));

  const columns: ColumnsType<ComparisonEvent> = [
    { title: '#', dataIndex: 'sequence_no', width: 58, fixed: 'left' },
    { title: '窗口', dataIndex: 'window_index', width: 68, render: (value) => value ? `W${value}` : '基线' },
    { title: '交易日', dataIndex: 'trade_date', width: 86, render: shortDate },
    { title: '标的日', dataIndex: 'target_date', width: 100 },
    {
      title: '意图', dataIndex: 'intent', width: 96,
      render: (value: ComparisonEvent['intent']) => (
        <Tag color={value === 'SIGNAL' ? 'cyan' : value === 'COMPLIANCE' ? 'gold' : undefined}>
          {result.role === 'seller' && value === 'COMPLIANCE' ? '远期操作' : INTENT_LABELS[value]}
        </Tag>
      ),
    },
    { title: '动作', dataIndex: 'action', width: 92, render: (value) => ACTION_LABELS[value as ComparisonEvent['action']] },
    { title: '成交 MWh', dataIndex: 'filled_quantity_mwh', align: 'right', width: 105, render: (value) => formatQuantity(value) },
    { title: '成交价', dataIndex: 'execution_price', align: 'right', width: 92, render: (value) => formatNumber(value) },
    { title: '价差', dataIndex: 'spread', align: 'right', width: 88, render: (value) => value == null ? '-' : formatSigned(value) },
    {
      title: '仓位前后', width: 125,
      render: (_, event) => `${(event.target_position_before * 100).toFixed(1)}% → ${(event.target_position_after * 100).toFixed(1)}%`,
    },
    { title: '已实现盈亏', dataIndex: 'realized_pnl_delta', align: 'right', width: 115, render: (value) => formatSigned(value) },
    {
      title: '交易理由', width: 330,
      render: (_, event) => event.constraint_reasons.length
        ? constraintText(event)
        : eventReason(event, result.role),
    },
  ];

  const countLabel = executionFilter === 'FILLED'
    ? `${events.length} 笔已成交`
    : executionFilter === 'UNEXECUTED'
      ? `${events.length} 笔受限/未执行`
      : `${events.length} 笔事件`;

  return (
    <div className="mtb-ledger" data-testid="event-ledger">
      <div className="mtb-ledger-head">
        <PortfolioSelector
          options={options}
          value={portfolio}
          onChange={(value) => {
            onPortfolioChange(value);
            setTargetDate('ALL');
            setWindowIndex('ALL');
          }}
          ariaPrefix="账本组合"
        />
        <strong>{selected.label} · {countLabel}</strong>
      </div>
      <div className="mtb-ledger-filters">
        <div className="mtb-portfolio-selector mtb-portfolio-selector--small" role="group" aria-label="账本事件筛选">
          {([
            ['FILLED', '已成交'],
            ['UNEXECUTED', '受限/未执行'],
            ['ALL', '全部'],
          ] as const).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={executionFilter === value ? 'is-active' : ''}
              aria-label={`账本事件 ${label}`}
              aria-pressed={executionFilter === value}
              onClick={() => setExecutionFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <Select
          aria-label="账本标的日"
          value={targetDate}
          onChange={setTargetDate}
          options={[{ value: 'ALL', label: '全部标的日' }, ...targetOptions]}
          style={{ width: 150 }}
        />
        <Select
          aria-label="账本窗口"
          value={windowIndex}
          onChange={setWindowIndex}
          options={[{ value: 'ALL', label: '全部窗口' }, ...windowOptions]}
          style={{ width: 130 }}
        />
      </div>
      <Table
        rowKey="event_id"
        dataSource={events}
        columns={columns}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: false }}
        scroll={{ x: 1360 }}
        locale={{ emptyText: '当前筛选下没有事件' }}
        expandable={{
          expandedRowRender: (event) => (
            <dl className="mtb-event-audit">
              <div><dt>原始组合</dt><dd>{event.portfolio_type}</dd></div>
              <div><dt>原始意图</dt><dd>{event.intent}</dd></div>
              <div><dt>原始触发</dt><dd>{event.trigger_reason}</dd></div>
              <div><dt>原始动作</dt><dd>{event.action}</dd></div>
              <div><dt>成本基础</dt><dd>{String(event.cost_basis_before)} → {String(event.cost_basis_after)}</dd></div>
              <div><dt>原始约束</dt><dd>{event.constraint_reasons.map((reason) => CONSTRAINT_LABELS[reason] ?? reason).join('、') || '-'}</dd></div>
            </dl>
          ),
        }}
      />
    </div>
  );
}
